import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { millisecondsInHour } from 'date-fns';
import { DrivingPolicyReportDto, StayOverTypeEnum } from 'api/model';
import { allOptions as stayOverTypeValues } from 'hooks/useStayOverTypeOptions';
import { DEFAULT_MAX_HOURS_PER_WEEK } from 'constants/AppConstants';
import { getHoursAndMinutesFromMillisecondsString } from 'utils/date-helpers';
import {
  getProgressLevel,
  getProgressWidthPercent,
} from 'pages/Employees/Availability/availability-helper';

const WeeklyHoursWarningRow = styled.div<{ level: 'warning' | 'over' }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85em;
  color: ${({ theme, level }) =>
    level === 'over'
      ? theme.colors.foreground.error
      : theme.colors.foreground.warning};
`;

// True only when a real overnight type (Hotell/Privat/Lastbil) is picked — not "NEJ". The "NEJ" option
// has value undefined and renders in the <option> as "", so reject empty/null first, then require the
// value to be an actual enum member. (HOTEL is 0 and Number("") is also 0, so a Number()-only check
// without the empty-guard would treat "NEJ" as HOTEL.)
export const isStayOverTypeSelected = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return false;
  return stayOverTypeValues.includes(Number(value) as StayOverTypeEnum);
};

// Warns, when an overnight stay is selected on an assignment, that the FT is near/over their weekly
// working-time cap — so a hotel booking may give little/no extra working time the next day (the ticket's
// "obetald tågresa"-scenario). Non-blocking on purpose: "veckans sista arbetsdag" can't be derived
// reliably (FT schedules vary, endOfWeek is Sunday ≠ the FT's last workday), so we inform, not gate.
// Uses the same percentage + thresholds as the availability progress bars. Rendered in both the edit
// form (useCaseAssignmentFormRows) and the read-mode view (CaseAssignment).
const WeeklyHoursStayOverWarning: React.FC<{
  stayOverTypeIdSelected: boolean;
  report: DrivingPolicyReportDto | undefined;
}> = ({ stayOverTypeIdSelected, report }) => {
  if (!stayOverTypeIdSelected || !report) return null;

  const maxPerWeek = report.maxHoursPerWeek ?? DEFAULT_MAX_HOURS_PER_WEEK;
  const widthPercent = getProgressWidthPercent(
    report.hoursDrivenThisWeek ?? 0,
    maxPerWeek
  );
  const level = getProgressLevel(widthPercent);
  if (level === 'good') return null;

  // Format from the unrounded hour values (as h/m, matching the driving-policy bar tooltips) so the
  // numbers never contradict the (unrounded) progress level — e.g. 47.6h must not render as "48/48 h,
  // 0 h kvar" while the level is still just "warning".
  const drivenHours = report.hoursDrivenThisWeek ?? 0;
  const driven = getHoursAndMinutesFromMillisecondsString(
    drivenHours * millisecondsInHour
  );
  // The cap is always whole hours (48 by default), so show it plainly without a "0m" suffix.
  const max = `${Math.round(maxPerWeek)}h`;
  const remaining = getHoursAndMinutesFromMillisecondsString(
    Math.max(0, maxPerWeek - drivenHours) * millisecondsInHour
  );

  return (
    <WeeklyHoursWarningRow level={level}>
      <FontAwesomeIcon icon={faWarning} />
      {level === 'over'
        ? `Fälttestaren har nått veckotaket (${driven}/${max}) — om detta är veckans sista arbetsdag ger övernattning sannolikt ingen extra arbetstid nästa dag.`
        : `Fälttestaren närmar sig veckotaket (${driven}/${max}, ~${remaining} kvar) — om detta är veckans sista arbetsdag, kontrollera att övernattning ger tillräckligt med arbetstid nästa dag.`}
    </WeeklyHoursWarningRow>
  );
};

export default WeeklyHoursStayOverWarning;
